import { useMemo, useState } from 'react';
import { Badge, EmptyState, InlineError, Skeleton } from '../../components/ui';
import { useCgOrders, useCgItems, useCgStatuses, useUpdateCgOrder } from './hooks';
import { useTapPeople } from '../projects/hooks';
import { cgErrorMessage, orderTotal, todayIso } from './types';
import { blockedStatuses, forwardOnlyReason } from './statusFlow';
import { initialsOf, pipelineColumns } from './pipeline';
import { filterOrdersByScope, statusLabelOf } from './pipelineScope';
import type { CgPipelineScope } from './pipelineScope';
import type { CgOrder, CgOrderItem } from './types';
import type { PipelineCard } from './pipeline';

/**
 * The pipeline board: every open order, in the column its status puts it in.
 *
 * ── Why the column IS the status, rather than tracking it ──────────────────
 *
 * There is no board state to keep in step with the orders table. A card's
 * column is read from cg_orders.status, the same field the table's dropdown
 * writes and the same field the supplier portal and the review portal move.
 * So an order that advances anywhere in the system has already moved on this
 * board by the time it renders. The alternative — a stage recorded separately
 * — would be a second truth to reconcile, and the first time they disagreed
 * nobody would know which to believe.
 *
 * Dragging a card writes that same field, so the board is not a shortcut past
 * any rule: the financial gate, the supplier guard and the no-going-backwards
 * guard all still apply, and a refusal surfaces here exactly as it does in the
 * table. A board that could drag an order past a money check would make the
 * check decorative.
 */

/** Days in one place before the board says something about it. */
const STALL_DAYS = 7;

function money(value: number): string {
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * The board draws ONE ladder.
 *
 * Its columns are the pipeline, so it takes a single pipeline rather than a
 * scope: the page never hands it 'all', because two ladders is not one board.
 * That rule lives in scopesForTab, which does not offer All outside Orders.
 */
export function CgPipeline({
  scope,
  onOpenOrder,
}: {
  scope: CgPipelineScope;
  onOpenOrder: (order: CgOrder) => void;
}) {
  const orders = useCgOrders();
  const items = useCgItems();
  const statuses = useCgStatuses(scope);
  const people = useTapPeople(true);
  const update = useUpdateCgOrder();

  const [error, setError] = useState('');
  const [dragging, setDragging] = useState<CgOrder | null>(null);
  const [overColumn, setOverColumn] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);

  const today = todayIso();
  const allOrders = useMemo(
    () => filterOrdersByScope(orders.data ?? [], scope),
    [orders.data, scope],
  );
  const allItems = useMemo(() => items.data ?? [], [items.data]);
  const allStatuses = useMemo(() => statuses.data ?? [], [statuses.data]);

  // Order value, computed once for every order rather than per card render.
  const totals = useMemo(() => {
    const byOrder = new Map<string, CgOrderItem[]>();
    for (const item of allItems) {
      const list = byOrder.get(item.order_id);
      if (list === undefined) {
        byOrder.set(item.order_id, [item]);
      } else {
        list.push(item);
      }
    }
    const map = new Map<string, number>();
    for (const order of allOrders) {
      map.set(order.id, orderTotal(order, byOrder.get(order.id) ?? []));
    }
    return map;
  }, [allOrders, allItems]);

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const person of people.data ?? []) {
      map.set(person.id, person.display_name === '' ? person.email : person.display_name);
    }
    return map;
  }, [people.data]);

  const columns = useMemo(
    () => pipelineColumns(allOrders, allStatuses, totals, today, STALL_DAYS, !showClosed),
    [allOrders, allStatuses, totals, today, showClosed],
  );

  if (orders.isLoading || items.isLoading || statuses.isLoading) {
    return <Skeleton lines={8} />;
  }
  // A failed query used to be indistinguishable from a successful empty one.
  //
  // Kyu opened the Pipeline and found every order stacked in a single column
  // headed "__unknown__" with no status columns at all. That board was not a
  // board: fetchCgStatuses had failed, react-query held no data, `?? []` made
  // it an empty vocabulary, and pipelineColumns did exactly what it is
  // supposed to do with an order whose status it does not recognise — filed
  // it under UNKNOWN_STATUS so nobody loses an order. Every part behaved
  // correctly and the screen was nonsense, because nothing above them ever
  // asked whether the data had arrived.
  //
  // The status vocabulary is seeded by migration and no screen can empty it,
  // so zero rows is not a state this application can legitimately be in —
  // it means the query was refused, most often because a column the select
  // names is missing from the database the browser is actually talking to.
  // Saying so is worth far more than rendering something board-shaped.
  if (statuses.isError || allStatuses.length === 0) {
    return (
      <InlineError>
        The order statuses could not be loaded, so the board has no columns to draw. This usually
        means the app is newer than the database — check that every migration has been run, then
        reload the page.
        {/* The database's own words, carried through rather than swallowed.
            "column cg_statuses.is_archived does not exist" names the missing
            migration in one line; a tidier generic sentence sends somebody to
            the browser console to find out what this one already knows. */}
        {statuses.error === null ? '' : ` (${statuses.error.message})`}
      </InlineError>
    );
  }
  if (orders.isError || items.isError) {
    return <InlineError>Orders could not be loaded. Reload the page to try again.</InlineError>;
  }
  if (allOrders.length === 0) {
    return (
      <EmptyState
        title="No orders yet"
        description="Orders appear here as soon as they exist, in the column their status puts them in."
      />
    );
  }

  function drop(statusId: string) {
    const order = dragging;
    setDragging(null);
    setOverColumn(null);
    if (order === null || order.status === statusId) {
      return;
    }
    // The same guard the dropdown uses. Checked here too because a drag has no
    // disabled state to lean on — there is nothing to grey out mid-gesture.
    if (blockedStatuses(order, allStatuses).has(statusId)) {
      setError(forwardOnlyReason(order, allStatuses));
      return;
    }
    setError('');
    update.mutate(
      { orderId: order.id, update: { status: statusId } },
      { onError: (err) => setError(cgErrorMessage(err.message)) },
    );
  }

  return (
    <div>
      <div className="cg-pipeline-toolbar">
        <label className="cg-pipeline-toggle">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(event) => setShowClosed(event.target.checked)}
          />
          Show closed orders
        </label>
        <span className="text-body-meta">
          Drag a card to move it. The financial gate and status rules still apply.
        </span>
      </div>
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="cg-pipeline">
        {columns.map((column) => (
          <section
            key={column.status.id}
            className={`cg-pipeline-column${overColumn === column.status.id ? ' cg-pipeline-column--over' : ''}`}
            onDragOver={(event) => {
              // Without preventDefault the browser refuses the drop outright.
              event.preventDefault();
              setOverColumn(column.status.id);
            }}
            onDragLeave={() =>
              setOverColumn((current) => (current === column.status.id ? null : current))
            }
            onDrop={() => drop(column.status.id)}
            aria-label={`${column.status.id}, ${String(column.cards.length)} orders`}
          >
            <header className="cg-pipeline-head">
              {/* cg_statuses.id IS the label — 'Order Placed', 'Shipped'. There is
                  no separate display column, and inventing one here would drift
                  from the dropdown that writes the same values. */}
              <span className="cg-pipeline-title">{statusLabelOf(column.status)}</span>
              <span className="cg-pipeline-count">{column.cards.length}</span>
              <span className="cg-pipeline-value">{money(column.value)}</span>
            </header>
            <ul className="cg-pipeline-cards">
              {column.cards.map((card) => (
                <PipelineTile
                  key={card.order.id}
                  card={card}
                  total={totals.get(card.order.id) ?? 0}
                  owner={
                    card.order.owner_id === null ? '' : (nameById.get(card.order.owner_id) ?? '')
                  }
                  onDragStart={() => setDragging(card.order)}
                  onDragEnd={() => {
                    setDragging(null);
                    setOverColumn(null);
                  }}
                  onOpen={() => onOpenOrder(card.order)}
                />
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function PipelineTile({
  card,
  total,
  owner,
  onDragStart,
  onDragEnd,
  onOpen,
}: {
  card: PipelineCard;
  total: number;
  owner: string;
  onDragStart: () => void;
  onDragEnd: () => void;
  onOpen: () => void;
}) {
  const initials = initialsOf(owner);
  return (
    <li>
      {/* A button, not a div with a click handler: the card has to be
          reachable and openable from the keyboard, and dragging is a mouse
          affordance that cannot be the only way in. */}
      <button
        type="button"
        className="cg-pipeline-card"
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onOpen}
      >
        <span className="cg-pipeline-card-head">
          <span className="cg-pipeline-order-no">{card.order.order_no}</span>
          <span className="cg-pipeline-total">{money(total)}</span>
        </span>
        <span className="cg-pipeline-client">{card.order.client}</span>
        {card.flags.length === 0 ? null : (
          <span className="cg-pipeline-flags">
            {card.flags.map((flag) => (
              <Badge
                key={flag.label}
                micro
                tone={
                  flag.tone === 'late' ? 'danger' : flag.tone === 'ready' ? 'success' : 'warning'
                }
              >
                {flag.label}
              </Badge>
            ))}
          </span>
        )}
        {initials === '' ? null : (
          <span className="cg-pipeline-owner" title={owner}>
            {initials}
          </span>
        )}
      </button>
    </li>
  );
}
